import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { Label } from './ui/label';
import { Input } from './ui/input';
import { RadioGroup, RadioGroupItem } from './ui/radio-group';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { ChevronUp, ChevronDown, Calendar } from 'lucide-react';
import { format } from 'date-fns';
import { showToast } from '../utils/toast';

const CustomRecurrenceModal = ({ 
  open, 
  onClose, 
  onSave,
  initialData = null 
}) => {
  const [repeatEvery, setRepeatEvery] = useState(1);
  const [repeatUnit, setRepeatUnit] = useState('week'); // day, week, month, year
  const [selectedDays, setSelectedDays] = useState([]); // Array of day indices: 0=Sunday, 1=Monday, etc.
  const [endType, setEndType] = useState('never'); // never, on, after
  const [endDate, setEndDate] = useState('');
  const [occurrences, setOccurrences] = useState(13);

  // Days of the week
  const daysOfWeek = [
    { label: 'S', value: 0, fullName: 'Sunday' },
    { label: 'M', value: 1, fullName: 'Monday' },
    { label: 'T', value: 2, fullName: 'Tuesday' },
    { label: 'W', value: 3, fullName: 'Wednesday' },
    { label: 'T', value: 4, fullName: 'Thursday' },
    { label: 'F', value: 5, fullName: 'Friday' },
    { label: 'S', value: 6, fullName: 'Saturday' }
  ];

  // Load initial data if provided
  useEffect(() => {
    if (initialData) {
      try {
        const data = typeof initialData === 'string' ? JSON.parse(initialData) : initialData;
        setRepeatEvery(data.repeatEvery || 1);
        setRepeatUnit(data.repeatUnit || 'week');
        setSelectedDays(data.selectedDays || []);
        setEndType(data.endType || 'never');
        setEndDate(data.endDate || '');
        setOccurrences(data.occurrences || 13);
      } catch (error) {
        console.error('Error parsing initial recurrence data:', error);
      }
    } else {
      // Reset to defaults
      setRepeatEvery(1);
      setRepeatUnit('week');
      setSelectedDays([]);
      setEndType('never');
      setEndDate('');
      setOccurrences(13);
    }
  }, [initialData, open]);

  // Auto-select current day when unit is "week" and no days are selected
  useEffect(() => {
    if (repeatUnit === 'week' && selectedDays.length === 0 && open) {
      const today = new Date().getDay();
      setSelectedDays([today]);
    }
  }, [repeatUnit, open]);

  const handleDayToggle = (dayValue) => {
    setSelectedDays(prev => {
      if (prev.includes(dayValue)) {
        return prev.filter(d => d !== dayValue);
      } else {
        return [...prev, dayValue].sort((a, b) => a - b);
      }
    });
  };

  const handleIncrement = (field) => {
    if (field === 'repeatEvery') {
      setRepeatEvery(prev => Math.min(prev + 1, 999));
    } else if (field === 'occurrences') {
      setOccurrences(prev => Math.min(prev + 1, 999));
    }
  };

  const handleDecrement = (field) => {
    if (field === 'repeatEvery') {
      setRepeatEvery(prev => Math.max(prev - 1, 1));
    } else if (field === 'occurrences') {
      setOccurrences(prev => Math.max(prev - 1, 1));
    }
  };

  const handleSave = () => {
    // Validate: if repeatUnit is 'week', at least one day must be selected
    if (repeatUnit === 'week' && selectedDays.length === 0) {
      showToast.error('Please select at least one day for weekly recurrence');
      return; // Don't save if no days selected
    }
    
    // Validate: if endType is 'on', endDate must be selected
    if (endType === 'on' && !endDate) {
      showToast.error('Please select an end date');
      return;
    }

    const customRecurrence = {
      repeatEvery,
      repeatUnit,
      selectedDays: repeatUnit === 'week' ? selectedDays : [],
      endType,
      endDate: endType === 'on' ? endDate : null,
      occurrences: endType === 'after' ? occurrences : null
    };

    console.log('✅ Custom recurrence saved:', customRecurrence);
    onSave(customRecurrence);
    showToast.success('Custom recurrence pattern saved');
  };

  const handleClose = () => {
    onClose();
  };

  const getTodayDate = () => {
    return new Date().toISOString().split('T')[0];
  };

  // Format date for display
  const formatDateDisplay = (dateString) => {
    if (!dateString) return '';
    try {
      const date = new Date(dateString);
      return format(date, 'MMM d, yyyy');
    } catch {
      return dateString;
    }
  };

  return (
    <Dialog open={open} onOpenChange={(isOpen) => {
      if (!isOpen) {
        handleClose();
      }
    }}>
      <DialogContent className="sm:max-w-[450px] w-[95vw] mx-auto flex  flex-col " onInteractOutside={(e) => e.preventDefault()}>
        <DialogHeader>
          <DialogTitle>Custom recurrence</DialogTitle>
          <DialogDescription >
            <p className="">
            Define a custom recurring schedule for this meeting
            </p>
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6 sm:py-4">
          {/* Repeat every */}
          <div className="space-y-2">
            <Label className="text-sm font-medium">Repeat every</Label>
            <div className="flex items-center gap-2">
              <div className="flex items-center border rounded-md">
                <Input
                  type="number"
                  min="1"
                  max="999"
                  value={repeatEvery}
                  onChange={(e) => {
                    const value = parseInt(e.target.value) || 1;
                    setRepeatEvery(Math.max(1, Math.min(999, value)));
                  }}
                  className="w-16 border-0 focus-visible:ring-0 text-center [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                />
                <div className="flex flex-col border-l">
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="h-4 w-6 p-0 rounded-none border-b"
                    onClick={() => handleIncrement('repeatEvery')}
                  >
                    <ChevronUp className="h-3 w-3" />
                  </Button>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="h-4 w-6 p-0 rounded-none"
                    onClick={() => handleDecrement('repeatEvery')}
                  >
                    <ChevronDown className="h-3 w-3" />
                  </Button>
                </div>
              </div>
              <Select value={repeatUnit} onValueChange={setRepeatUnit}>
                <SelectTrigger className="flex-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="day">day</SelectItem>
                  <SelectItem value="week">week</SelectItem>
                  <SelectItem value="month">month</SelectItem>
                  <SelectItem value="year">year</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Repeat on (only for week) */}
          {repeatUnit === 'week' && (
            <div className="space-y-2">
              <Label className="text-sm font-medium">Repeat on</Label>
              <div className="flex gap-1">
                {daysOfWeek.map((day) => {
                  const isSelected = selectedDays.includes(day.value);
                  return (
                    <Button
                      key={day.value}
                      type="button"
                      variant={isSelected ? "default" : "outline"}
                      size="sm"
                      className={`h-10 w-10 rounded-full p-0 ${
                        isSelected 
                          ? 'bg-blue-600 hover:bg-blue-700 text-white' 
                          : 'bg-white hover:bg-gray-50 text-gray-700'
                      }`}
                      onClick={() => handleDayToggle(day.value)}
                      title={day.fullName}
                    >
                      {day.label}
                    </Button>
                  );
                })}
              </div>
            </div>
          )}

          {/* Ends */}
          <div className="space-y-3">
            <Label className="text-sm font-medium">Ends</Label>
            <RadioGroup value={endType} onValueChange={setEndType} >
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="never" className="[&>span>svg]:fill-blue-600" id="never" />
                <Label htmlFor="never" className="font-normal cursor-pointer">
                  Never
                </Label>
              </div>
              
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="on" className="[&>span>svg]:fill-blue-600" id="on" />
                <Label htmlFor="on" className="font-normal cursor-pointer">
                  On
                </Label>
                <div className="flex items-center gap-2">
                  <Input
                    type="date"
                    value={endDate}
                    onChange={(e) => setEndDate(e.target.value)}
                    min={getTodayDate()}
                    disabled={endType !== 'on'}
                    className={`w-40 ${endType !== 'on' ? 'opacity-50 cursor-not-allowed' : ''}`}
                  />
                  {endType === 'on' && endDate && (
                    <span className="text-sm text-gray-500">
                      {formatDateDisplay(endDate)}
                    </span>
                  )}
                </div>
              </div>
              
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="after" className="[&>span>svg]:fill-blue-600" id="after" />
                <Label htmlFor="after" className="font-normal cursor-pointer">
                  After
                </Label>
                <div className="flex items-center border rounded-md ml-2">
                  <Input
                    type="number"
                    min="1"
                    max="999"
                    value={occurrences}
                    onChange={(e) => {
                      const value = parseInt(e.target.value) || 1;
                      setOccurrences(Math.max(1, Math.min(999, value)));
                    }}
                    disabled={endType !== 'after'}
                    className={`w-20 border-0 focus-visible:ring-0 text-center [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none ${
                      endType !== 'after' ? 'opacity-50 cursor-not-allowed' : ''
                    }`}
                  />
                  <div className="flex flex-col border-l">
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className="h-4 w-6 p-0 rounded-none border-b"
                      onClick={() => handleIncrement('occurrences')}
                      disabled={endType !== 'after'}
                    >
                      <ChevronUp className="h-3 w-3" />
                    </Button>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className="h-4 w-6 p-0 rounded-none"
                      onClick={() => handleDecrement('occurrences')}
                      disabled={endType !== 'after'}
                    >
                      <ChevronDown className="h-3 w-3" />
                    </Button>
                  </div>
                </div>
                <span className="text-sm text-gray-500 ml-2">
                  {occurrences} occurrence{occurrences !== 1 ? 's' : ''}
                </span>
              </div>
            </RadioGroup>
          </div>
        </div>

        <div className="flex justify-end gap-3 pt-4 border-t">
          <Button variant="outline" onClick={handleClose}>
            Cancel
          </Button>
          <Button 
            onClick={handleSave}
            disabled={repeatUnit === 'week' && selectedDays.length === 0}
            className="bg-blue-600 hover:bg-blue-700"
          >
            Done
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default CustomRecurrenceModal;

